// Copyright (C) Okahu Inc 2023-2024. All rights reserved.

import { useCallback, useEffect, useState } from 'react';
import { measuresManager } from '@/services/measuresManager';
import { MeasuresData } from 'types/measures-api-data';

import { usePresets } from '@/components/display-page/prompts/date-range-picker/calendar-helper/presets';

// Function to fetch measure data from the localstorage.
export const useMeasuresFromLocalStorage = () => {
  const [cardMeasures, setCardMeasures] = useState<MeasuresData>({});
  const [loading, setLoading] = useState(false);
  const presets = usePresets();

  // Function to read from localStorage
  const loadFromLocalStorage = useCallback(() => {
    const cardMeasuresStrData = localStorage.getItem('measuresList');
    let cardMeasuresData: MeasuresData = {};

    try {
      cardMeasuresData = JSON.parse(cardMeasuresStrData || '{}');
    } catch (e) {
      cardMeasuresData = {};
    }

    setCardMeasures(cardMeasuresData);
  }, []);

  // Manual refresh function
  const refreshMeasures = useCallback(
    async (appNames: string[]) => {
      setLoading(true);
      await measuresManager.refresh(appNames, presets);
      // measuresUpdated event will trigger loadFromStorage
    },
    [presets]
  );

  useEffect(() => {
    // Initial load from cache
    loadFromLocalStorage();

    // Listen for updates from the manager
    const handleMeasuresUpdate = () => {
      loadFromLocalStorage();
      setLoading(false);
    };

    // Listen for loading state changes
    const handleLoadingChange = (event: Event) => {
      const customEvent = event as CustomEvent<boolean>;
      setLoading(customEvent.detail);
    };

    window.addEventListener('measuresUpdated', handleMeasuresUpdate);
    window.addEventListener(
      'measuresLoading',
      handleLoadingChange as EventListener
    );

    return () => {
      window.removeEventListener('measuresUpdated', handleMeasuresUpdate);
      window.removeEventListener(
        'measuresLoading',
        handleLoadingChange as EventListener
      );
    };
  }, [JSON.stringify(loadFromLocalStorage)]);

  return { cardMeasures, loading, refreshMeasures };
};
